import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router";
import {
  Bell,
  BellOff,
  BellRing,
  Check,
  ChevronRight,
  FileText,
  Monitor,
  Moon,
  Palette,
  Printer,
  Settings,
  RefreshCw,
  Sun,
} from "lucide-react";
import { usePrintConf } from "@pos/shared/query";
import { usePrintStore, useThemeStore } from "@pos/shared/stores";
import type { ThemeMode } from "@pos/shared/stores";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from "@pos/ui";
import {
  fireTestNotification,
  getNotificationPermission,
  isNotificationsSupported,
  requestNotificationPermission,
  type NotificationPermissionState,
} from "@/lib/notifications";
import { THEME_PRESETS } from "@/lib/theme";

const MODE_OPTIONS: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

interface PrinterInfo {
  name: string;
  isDefault: boolean;
}

const PAPER_WIDTH_ITEMS = [
  { label: "80mm", value: "80" },
  { label: "74mm", value: "74" },
  { label: "70mm", value: "70" },
  { label: "58mm", value: "58" },
] as const;

const RECEIPT_DESIGN_ITEMS = [
  { label: "Classic", value: "classic" },
  { label: "Stacked", value: "stacked" },
] as const;

const FONT_ITEMS = [
  { label: "Font A (universal)", value: "A" },
  { label: "Font B (denser)", value: "B" },
] as const;

export function SettingsPage() {
  const printConf = usePrintConf();
  const conf = printConf[0] as Record<string, unknown> | undefined;

  const mode = usePrintStore((s) => s.mode);
  const paperWidth = usePrintStore((s) => s.paperWidth);
  const printerName = usePrintStore((s) => s.printerName);
  const reportsPrinterName = usePrintStore((s) => s.reportsPrinterName);
  const receiptDesign = usePrintStore((s) => s.receiptDesign);
  const font = usePrintStore((s) => s.font);
  const kotAutoPrint = usePrintStore((s) => s.kotAutoPrint);
  const setMode = usePrintStore((s) => s.setMode);
  const setPaperWidth = usePrintStore((s) => s.setPaperWidth);
  const setPrinterName = usePrintStore((s) => s.setPrinterName);
  const setReportsPrinterName = usePrintStore((s) => s.setReportsPrinterName);
  const setReceiptDesign = usePrintStore((s) => s.setReceiptDesign);
  const setFont = usePrintStore((s) => s.setFont);
  const setKotAutoPrint = usePrintStore((s) => s.setKotAutoPrint);

  const themeMode = useThemeStore((s) => s.mode);
  const themePreset = useThemeStore((s) => s.preset);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const setThemePreset = useThemeStore((s) => s.setPreset);

  const isDesktop = !!window.electron;

  // Printer list for desktop
  const [printers, setPrinters] = useState<PrinterInfo[]>([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // Notification permission. We refresh on mount + after the user
  // hits the Test button so the card label reflects the live state.
  const [notifPerm, setNotifPerm] = useState<NotificationPermissionState>(
    getNotificationPermission(),
  );

  const refreshNotifPerm = () => setNotifPerm(getNotificationPermission());

  const handleTestNotification = async () => {
    if (!isNotificationsSupported()) {
      toast.error("Notifications aren't available in this environment.");
      return;
    }
    if (Notification.permission === "denied") {
      toast.error(
        "Notifications are blocked. Enable them in your system settings.",
      );
      return;
    }
    if (Notification.permission === "default") {
      const next = await requestNotificationPermission();
      setNotifPerm(next);
      if (next !== "granted") {
        toast.error(
          "Permission not granted — try enabling in system settings.",
        );
        return;
      }
    }
    const ok = fireTestNotification();
    if (!ok) {
      toast.error("Couldn't fire a notification — check system permissions.");
    } else {
      refreshNotifPerm();
    }
  };

  const printerSelectItems = useMemo(
    () => [
      { label: "System Default", value: "__default__" },
      ...printers.map((p) => ({
        label: p.name + (p.isDefault ? " (default)" : ""),
        value: p.name,
      })),
    ],
    [printers],
  );

  const refreshPrinters = () => {
    if (!window.electron?.getPrinters) return;
    setLoadingPrinters(true);
    window.electron
      .getPrinters()
      .then((list) => {
        setPrinters(
          (list as { name: string; isDefault: boolean }[]).map((p) => ({
            name: p.name,
            isDefault: p.isDefault,
          })),
        );
      })
      .finally(() => setLoadingPrinters(false));
  };

  useEffect(() => {
    if (isDesktop) refreshPrinters();
  }, [isDesktop]);

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div className="flex items-center gap-2">
        <Settings className="size-6 text-primary" />
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
      </div>

      {/* Audit Log */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">Audit Log</CardTitle>
          </div>
          <CardDescription>
            View user activity and transaction history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link
            to="/audit-log"
            className="flex items-center justify-between rounded-md border px-4 py-3 text-base transition-colors hover:bg-accent"
          >
            <div>
              <p className="font-medium">View Audit Log</p>
              <p className="text-sm text-muted-foreground">
                Browse, search, and export daily audit entries
              </p>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
          </Link>
        </CardContent>
      </Card>

      {/* Developer Tools — hidden during the pilot. Restore the card
          when access-control surfaces are exposed to operators again. */}
      {/*
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bug className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">Developer Tools</CardTitle>
          </div>
          <CardDescription>Debugging and diagnostic utilities</CardDescription>
        </CardHeader>
        <CardContent>
          <Link
            to="/debug/access"
            className="flex items-center justify-between rounded-md border px-4 py-3 text-base transition-colors hover:bg-accent"
          >
            <div>
              <p className="font-medium">Access Control Debug</p>
              <p className="text-sm text-muted-foreground">
                View modules, reports, and shortcuts for the current profile
              </p>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
          </Link>
        </CardContent>
      </Card>
      */}

      {/* Appearance */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Palette className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">Appearance</CardTitle>
          </div>
          <CardDescription>
            Light/dark mode and brand color — saved per terminal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Mode</Label>
            <div className="flex gap-2">
              {MODE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = themeMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setThemeMode(opt.value)}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors ${
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:bg-accent"
                    }`}
                    aria-pressed={active}
                  >
                    <Icon className="size-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {THEME_PRESETS.map((preset) => {
                const active = themePreset === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setThemePreset(preset.id)}
                    className={`relative size-10 rounded-full border-2 transition-transform hover:scale-105 ${
                      active
                        ? "border-foreground"
                        : "border-transparent ring-1 ring-border"
                    }`}
                    style={{ backgroundColor: preset.swatch }}
                    aria-label={preset.label}
                    aria-pressed={active}
                    title={preset.label}
                  >
                    {active && (
                      <Check className="absolute inset-0 m-auto size-4 text-white drop-shadow-sm" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">Notifications</CardTitle>
          </div>
          <CardDescription>
            Desktop alerts when teammates update tables on this shop
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <NotificationStatusIcon state={notifPerm} />
              <div>
                <p className="text-sm font-medium">
                  {notifPerm === "granted"
                    ? "Enabled"
                    : notifPerm === "denied"
                      ? "Blocked"
                      : notifPerm === "unsupported"
                        ? "Unavailable"
                        : "Not yet enabled"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {notifPerm === "denied"
                    ? "Allow notifications in your system settings to re-enable"
                    : notifPerm === "unsupported"
                      ? "This device doesn't support browser notifications"
                      : "Send a test alert to verify your setup"}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestNotification}
              disabled={notifPerm === "unsupported" || notifPerm === "denied"}
            >
              <Bell className="mr-1.5 size-3.5" />
              Test Notification
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Print Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Printer className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">Print Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure receipt printing preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* ESC/POS toggle — desktop only */}
          {isDesktop && (
            <div className="flex items-center justify-between">
              <div>
                <Label>ESC/POS Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Send raw ESC/POS commands to thermal printer
                </p>
              </div>
              <Switch
                checked={mode === "escpos"}
                onCheckedChange={(checked) =>
                  setMode(checked ? "escpos" : "html")
                }
              />
            </div>
          )}

          {/* Paper width — only when ESC/POS is enabled */}
          {isDesktop && mode === "escpos" && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Paper Width</Label>
                <p className="text-sm text-muted-foreground">
                  Thermal printer paper width
                </p>
              </div>
              <Select
                value={String(paperWidth)}
                onValueChange={(v) =>
                  v && setPaperWidth(Number(v) as 58 | 70 | 74 | 80)
                }
                items={PAPER_WIDTH_ITEMS}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAPER_WIDTH_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Receipt design — only when ESC/POS is enabled */}
          {isDesktop && mode === "escpos" && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Receipt Design</Label>
                <p className="text-sm text-muted-foreground">
                  Layout style for printed receipts
                </p>
              </div>
              <Select
                value={receiptDesign}
                onValueChange={(v) =>
                  v !== null && setReceiptDesign(v as "classic" | "stacked")
                }
                items={RECEIPT_DESIGN_ITEMS}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECEIPT_DESIGN_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Font — only when ESC/POS is enabled */}
          {isDesktop && mode === "escpos" && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Font</Label>
                <p className="text-sm text-muted-foreground">
                  Font B fits more per line; cheap printers may fall back to A
                </p>
              </div>
              <Select
                value={font}
                onValueChange={(v) => v !== null && setFont(v as "A" | "B")}
                items={FONT_ITEMS}
              >
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Printer selection — desktop only */}
          {isDesktop && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Printer</Label>
                <p className="text-sm text-muted-foreground">
                  Select the printer for receipts
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={printerName || "__default__"}
                  onValueChange={(v) =>
                    v !== null && setPrinterName(v === "__default__" ? "" : v)
                  }
                  items={printerSelectItems}
                >
                  <SelectTrigger className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {printerSelectItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={refreshPrinters}
                  disabled={loadingPrinters}
                  className="size-8 shrink-0"
                >
                  <RefreshCw
                    className={`size-4 ${loadingPrinters ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>
          )}

          {/* Reports printer — desktop only. Separate device from the
              receipt printer: reports are A4 pages, receipts are thermal.
              When unset the OS default printer is used; when no printer is
              available the Reports screen offers PDF download instead. */}
          {isDesktop && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Reports Printer</Label>
                <p className="text-sm text-muted-foreground">
                  Printer for full-page report output (A4)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={reportsPrinterName || "__default__"}
                  onValueChange={(v) =>
                    v !== null &&
                    setReportsPrinterName(v === "__default__" ? "" : v)
                  }
                  items={printerSelectItems}
                >
                  <SelectTrigger className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {printerSelectItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={refreshPrinters}
                  disabled={loadingPrinters}
                  className="size-8 shrink-0"
                >
                  <RefreshCw
                    className={`size-4 ${loadingPrinters ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>
          )}

          {/* Auto-print KOT requests — desktop only. Only one terminal
              per shop should opt in (the one connected to the kitchen
              printer); multiple terminals with this on would all race
              to print the same KOT. */}
          {isDesktop && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-print KOT requests</Label>
                <p className="text-sm text-muted-foreground">
                  Print waiter-app KOT requests automatically. Enable on the
                  terminal connected to the kitchen printer; keep the Table
                  Order page open.
                </p>
              </div>
              <Switch
                checked={kotAutoPrint}
                onCheckedChange={(checked) => setKotAutoPrint(checked)}
              />
            </div>
          )}

          {/* Server print config (read-only) */}
          {conf && (
            <div className="space-y-2">
              <Label>Server Print Config</Label>
              <div className="rounded-md border bg-muted/30 p-3">
                <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                  {Boolean(conf.fontFamily) && (
                    <ConfigRow label="Font" value={String(conf.fontFamily)} />
                  )}
                  {Boolean(conf.headerFont) && (
                    <ConfigRow
                      label="Header Font"
                      value={String(conf.headerFont)}
                    />
                  )}
                  {Boolean(conf.defaultFont) && (
                    <ConfigRow
                      label="Default Font"
                      value={String(conf.defaultFont)}
                    />
                  )}
                  {Boolean(conf.dpi) && (
                    <ConfigRow label="DPI" value={String(conf.dpi)} />
                  )}
                  {Boolean(conf.letterSpacing) && (
                    <ConfigRow
                      label="Letter Spacing"
                      value={String(conf.letterSpacing)}
                    />
                  )}
                  {Boolean(conf.lineHeight) && (
                    <ConfigRow
                      label="Line Height"
                      value={String(conf.lineHeight)}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </>
  );
}

function NotificationStatusIcon({
  state,
}: {
  state: NotificationPermissionState;
}) {
  if (state === "granted") {
    return (
      <BellRing className="size-4 text-emerald-600 dark:text-emerald-400" />
    );
  }
  if (state === "denied" || state === "unsupported") {
    return <BellOff className="size-4 text-rose-600 dark:text-rose-400" />;
  }
  return <Bell className="size-4 text-muted-foreground" />;
}
